import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Star, ChatText, User, ForkKnife, TrendUp, PaperPlaneTilt, CursorClick } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const FeedbackDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Review-request analytics — populated in parallel with feedback data.
  const [reviewAnalytics, setReviewAnalytics] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState(30);
  const [manualCount, setManualCount] = useState({ count: 0, note: "" });
  const [manualDraft, setManualDraft] = useState("");

  useEffect(() => { fetchData(); }, []);
   
  useEffect(() => { fetchReviewAnalytics(analyticsRange); }, [analyticsRange]);

  const fetchData = async () => {
    try {
      const [sumRes, fbRes, mcRes] = await Promise.all([
        axios.get(`${API}/feedback/summary`),
        axios.get(`${API}/feedback`),
        axios.get(`${API}/reports/review-analytics/manual-count`),
      ]);
      setSummary(sumRes.data);
      setFeedbacks(fbRes.data);
      setManualCount(mcRes.data);
      setManualDraft(String(mcRes.data?.count ?? ""));
    } catch (error) {
      console.error("Failed to load feedback:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewAnalytics = async (days) => {
    try {
      const res = await axios.get(`${API}/reports/review-analytics?days=${days}`);
      setReviewAnalytics(res.data);
    } catch (e) {
      // Silent — feature may be unused, don't spam toasts on every dashboard load.
    }
  };

  const saveManualCount = async () => {
    const c = Math.max(0, parseInt(manualDraft) || 0);
    try {
      const res = await axios.put(`${API}/reports/review-analytics/manual-count`, { count: c, note: manualCount.note || "" });
      setManualCount(res.data);
      toast.success("Google review count saved");
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const ratingDistData = summary ? Object.entries(summary.rating_distribution).map(([stars, count]) => ({
    stars: `${stars} Star${stars !== "1" ? "s" : ""}`,
    count
  })) : [];

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} size={14} weight={s <= rating ? "fill" : "regular"} className={s <= rating ? "text-yellow-500" : "text-border"} />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="feedback-dashboard">
      <Sidebar />
      <main className="main-content p-6">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-medium">Customer Feedback</h1>
          <p className="text-muted-foreground">Ratings, reviews, and performance insights</p>
        </div>

        {/* ===== Review-request analytics (always visible) ===== */}
        <Card className="mb-6" data-testid="review-analytics-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Star size={18} weight="fill" className="text-yellow-500" />
                Review-Request Analytics
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                How many customers clicked the review link vs how many we asked (last {analyticsRange} days).
              </p>
            </div>
            <div className="flex gap-1" data-testid="analytics-range-picker">
              {[7, 30, 90].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={analyticsRange === d ? "default" : "outline"}
                  onClick={() => setAnalyticsRange(d)}
                  data-testid={`range-${d}d`}
                  className="h-8 px-3 text-xs"
                >
                  {d}d
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="rounded-lg border p-3">
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                  <PaperPlaneTilt size={12} /> Requests Sent
                </div>
                <p className="font-mono text-2xl font-medium" data-testid="stat-sent">{reviewAnalytics?.total_sent ?? 0}</p>
              </div>
              <div className="rounded-lg border p-3">
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                  <CursorClick size={12} /> Link Clicks
                </div>
                <p className="font-mono text-2xl font-medium" data-testid="stat-clicked">{reviewAnalytics?.total_clicked ?? 0}</p>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">CTR</div>
                <p className="font-mono text-2xl font-medium" data-testid="stat-ctr">{reviewAnalytics?.ctr_percent ?? 0}%</p>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground mb-1">Google Reviews (manual)</div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    value={manualDraft}
                    onChange={(e) => setManualDraft(e.target.value)}
                    className="font-mono h-9 text-xl w-20"
                    data-testid="manual-count-input"
                  />
                  <Button size="sm" variant="outline" onClick={saveManualCount} className="h-9 text-xs" data-testid="save-manual-count">Save</Button>
                </div>
                {reviewAnalytics?.total_sent > 0 && manualCount?.count > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {manualCount.count > reviewAnalytics.total_sent
                      ? `${(manualCount.count / reviewAnalytics.total_sent).toFixed(1)}× per request`
                      : `${((manualCount.count / reviewAnalytics.total_sent) * 100).toFixed(1)}% conversion`}
                  </p>
                )}
              </div>
            </div>

            {reviewAnalytics?.series?.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={reviewAnalytics.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2DED7" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(d) => d.slice(5)}
                    interval={Math.max(0, Math.floor(analyticsRange / 10))}
                  />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2DED7", borderRadius: "8px", fontSize: "12px" }}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="sent" fill="#94A3B8" name="Sent" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="clicked" fill="#D29856" name="Clicked" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}

            {reviewAnalytics?.total_sent === 0 && (
              <p className="text-center text-sm text-muted-foreground py-6" data-testid="no-analytics-empty">
                No review requests sent in the last {analyticsRange} days. Enable review requests in Settings → Notifications → Review Request Follow-up.
              </p>
            )}

            {reviewAnalytics?.recent?.length > 0 && (
              <div className="mt-6" data-testid="recent-requests-list">
                <p className="text-xs font-medium text-muted-foreground mb-2">Recent requests</p>
                <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
                  {reviewAnalytics.recent.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div>
                        <p className="font-medium">{r.customer_name || "Guest"} <span className="text-muted-foreground text-xs capitalize">· {r.fulfillment_type}</span></p>
                        <p className="text-xs text-muted-foreground">
                          Sent {r.review_request_sent_at?.slice(0, 16).replace("T", " ")}
                          {r.review_first_click_at && (
                            <> · <span className="text-primary">clicked ×{r.review_click_count}</span></>
                          )}
                        </p>
                      </div>
                      <span className="font-mono text-xs">${(r.total || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !summary || summary.total_reviews === 0 ? (
          <Card>
            <CardContent className="text-center py-16 text-muted-foreground">
              <Star size={48} className="mx-auto mb-2 opacity-30" />
              <p className="text-lg">No feedback yet</p>
              <p className="text-sm">Customer feedback will appear here after they submit reviews</p>
              <p className="text-xs mt-4 bg-secondary/50 rounded-lg p-3 max-w-md mx-auto">
                Feedback URL: <code className="text-primary">/feedback?order=ORDER_ID</code>
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <Card><CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">Total Reviews</p>
                <p className="font-mono text-3xl font-medium">{summary.total_reviews}</p>
              </CardContent></Card>
              <Card className="bg-yellow-50/50 border-yellow-200"><CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">Overall</p>
                <div className="flex items-center justify-center gap-2">
                  <Star size={24} weight="fill" className="text-yellow-500" />
                  <span className="font-mono text-3xl font-medium">{summary.avg_overall}</span>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">Food</p>
                <div className="flex items-center justify-center gap-2">
                  <ForkKnife size={20} className="text-primary" />
                  <span className="font-mono text-3xl font-medium">{summary.avg_food || "-"}</span>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">Service</p>
                <div className="flex items-center justify-center gap-2">
                  <User size={20} className="text-accent" />
                  <span className="font-mono text-3xl font-medium">{summary.avg_service || "-"}</span>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground mb-1">Ambiance</p>
                <div className="flex items-center justify-center gap-2">
                  <TrendUp size={20} className="text-[#D29856]" />
                  <span className="font-mono text-3xl font-medium">{summary.avg_ambiance || "-"}</span>
                </div>
              </CardContent></Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Rating Distribution */}
              <Card>
                <CardHeader><CardTitle className="font-heading text-lg">Rating Distribution</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={ratingDistData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2DED7" />
                      <XAxis dataKey="stars" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2DED7', borderRadius: '8px' }} />
                      <Bar dataKey="count" fill="#D29856" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top Servers */}
              <Card>
                <CardHeader><CardTitle className="font-heading text-lg">Top Servers</CardTitle></CardHeader>
                <CardContent>
                  {summary.top_servers.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No server ratings yet</p>
                  ) : (
                    <div className="space-y-3">
                      {summary.top_servers.map((server, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center font-mono text-sm font-bold text-primary">
                              {idx + 1}
                            </div>
                            <span className="font-medium">{server.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {renderStars(Math.round(server.avg_rating))}
                            <span className="font-mono text-sm">{server.avg_rating}</span>
                            <Badge variant="outline" className="text-xs">{server.review_count} reviews</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top Items */}
            {summary.top_items.length > 0 && (
              <Card className="mb-6">
                <CardHeader><CardTitle className="font-heading text-lg">Top Rated Dishes</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {summary.top_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                        <span className="font-medium text-sm">{item.name}</span>
                        <div className="flex items-center gap-1">
                          <Star size={14} weight="fill" className="text-yellow-500" />
                          <span className="font-mono text-sm">{item.avg_rating}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Comments */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Recent Reviews</CardTitle></CardHeader>
              <CardContent>
                {feedbacks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No reviews yet</p>
                ) : (
                  <div className="space-y-3">
                    {feedbacks.slice(0, 20).map((fb) => (
                      <div key={fb.id} className="p-4 bg-secondary/30 rounded-xl border border-border" data-testid={`feedback-${fb.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{fb.guest_name || "Anonymous"}</span>
                            <span className="text-xs text-muted-foreground">{fb.table_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {renderStars(fb.overall_rating)}
                            {fb.server_name && fb.server_name !== "Self-Order" && (
                              <Badge variant="outline" className="text-xs">Server: {fb.server_name}</Badge>
                            )}
                          </div>
                        </div>
                        {fb.comment && <p className="text-sm text-muted-foreground italic">"{fb.comment}"</p>}
                        <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                          {fb.food_rating && <span>Food: {fb.food_rating}/5</span>}
                          {fb.service_rating && <span>Service: {fb.service_rating}/5</span>}
                          {fb.ambiance_rating && <span>Ambiance: {fb.ambiance_rating}/5</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default FeedbackDashboard;
